import { Dialog, Button, Flex, Text } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import styles from './BranchLiveDialog.module.css';
import { useBranchStore } from '../../stores/branchStore';

interface BranchLiveDialogProps {
  open: boolean;
  branchName?: string;
  /**
   * Branch the celebration is for. The branch is already archived by the
   * publish flow, so we deliberately don't navigate INTO it — see
   * `handleView` below. The id is retained for telemetry / future deep
   * links (e.g. a "see the diff" affordance).
   */
  branchId?: string;
  /**
   * Slug of the first published content piece from the branch, surfaced
   * by `BranchLivePresenter` via the branch-comparison query. When
   * present, "View it" navigates straight to `/library/{slug}` so the
   * author lands on something they just shipped instead of the library
   * landing.
   */
  firstContentSlug?: string | null;
  onOpenChange: (open: boolean) => void;
}

const SPARKLES = [
  { x: -78, y: -58, size: 26, s: 1.0, d: 0.55, rot: 18 },
  { x: 82, y: -42, size: 32, s: 1.0, d: 0.42, rot: -22 },
  { x: 68, y: 62, size: 22, s: 1.0, d: 0.70, rot: 14 },
  { x: -72, y: 64, size: 28, s: 1.0, d: 0.60, rot: -16 },
  { x: 4, y: -88, size: 18, s: 1.0, d: 0.80, rot: 25 },
  { x: -98, y: 6, size: 16, s: 1.0, d: 0.88, rot: -12 },
  { x: 98, y: 14, size: 20, s: 1.0, d: 0.50, rot: 20 },
  { x: 34, y: -70, size: 14, s: 1.0, d: 0.95, rot: -28 },
];

const SPECKS = [
  { x: -52, y: -82, d: 0.72 },
  { x: 56, y: -78, d: 0.84 },
  { x: -94, y: 44, d: 0.66 },
  { x: 92, y: -10, d: 0.92 },
  { x: 44, y: 84, d: 0.78 },
  { x: -40, y: 88, d: 1.00 },
  { x: 110, y: 44, d: 0.58 },
];

function SparkleStar({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0 C13 7 17 11 24 12 C17 13 13 17 12 24 C11 17 7 13 0 12 C7 11 11 7 12 0 Z" />
    </svg>
  );
}

export function BranchLiveDialog({
  open,
  branchName,
  branchId: _branchId,
  firstContentSlug,
  onOpenChange,
}: BranchLiveDialogProps) {
  const navigate = useNavigate();
  const setCurrentBranch = useBranchStore((s) => s.setCurrentBranch);

  const handleView = () => {
    onOpenChange(false);
    // After publish, the branch is auto-archived. The author wants to see
    // their changes live on MAIN, not inside the archived branch (where
    // the diff view + branch chrome would still be active). Drop any
    // currentBranch in the Zustand store first — otherwise Library.tsx's
    // `isInBranchMode = currentBranch !== null || !!branchId` short-
    // circuits the published slug fetch and the page would render the
    // previous branch context for a frame (same idiom as the search-
    // result navigation in SearchTray.tsx).
    setCurrentBranch(null);
    // Navigate to the first published article when we have its slug.
    // Falls back to /library when the comparison hasn't loaded yet, or
    // when the branch only touched landing pages (which don't have a
    // /:slug route).
    if (firstContentSlug) {
      navigate(`/library/${firstContentSlug}`);
    } else {
      navigate('/library');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="420px"
        aria-describedby={undefined}
        // Re-mount the stage on each open so CSS one-shot animations replay.
        key={open ? 'open' : 'closed'}
      >
        <Dialog.Title style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
          Your branch is live
        </Dialog.Title>

        <div className={styles.stage} aria-hidden="true">
          <div className={styles.pulse} />
          <div className={styles.glow} />
          <div className={styles.ring} />

          <div className={styles.pageIcon}>
            <div className={styles.pageIconInner}>
              <div className={styles.page}>
                <span className={`${styles.ln} ${styles.ln1}`} />
                <span className={`${styles.ln} ${styles.ln2}`} />
                <span className={`${styles.ln} ${styles.ln3}`} />
                <span className={`${styles.ln} ${styles.ln4}`} />
              </div>
            </div>
          </div>

          {SPARKLES.map((sp, i) => (
            <span
              key={`sp-${i}`}
              className={styles.sparkle}
              style={
                {
                  '--x': `${sp.x}px`,
                  '--y': `${sp.y}px`,
                  '--s': sp.s,
                  '--d': `${sp.d}s`,
                  '--rot': `${sp.rot}deg`,
                } as React.CSSProperties
              }
            >
              <SparkleStar size={sp.size} />
            </span>
          ))}

          {SPECKS.map((sk, i) => (
            <span
              key={`sk-${i}`}
              className={styles.speck}
              style={
                {
                  '--x': `${sk.x}px`,
                  '--y': `${sk.y}px`,
                  '--d': `${sk.d}s`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <div className={styles.caption}>
          <div className={styles.eyebrow}>All set</div>
          <div className={styles.title}>Your branch is live</div>
          {branchName && (
            <Text as="p" className={styles.subtitle}>
              “{branchName}” has been published to the main library.
            </Text>
          )}
        </div>

        <Flex gap="3" mt="5" justify="center">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Dismiss
            </Button>
          </Dialog.Close>
          <Button onClick={handleView}>View it</Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default BranchLiveDialog;
